import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { setTitle } from "../../../redux/slices/configuracoes/configuracoesSlice";
import { Col, DatePicker, DatePickerProps, Form, Row, Select, Image, Table, Button, Flex } from "antd";
import { setBuscaPrestacaoConta } from "../../../redux/slices/prestacaoConta/prestacaoContaSlice";
import { ColumnsType } from "antd/es/table";
import { SaldoConsignatarioModel } from "../../../models/lancamentoConsignatario/saldoConsignatarioModel";
import { currencyBRL } from "../../../util/dinheiroBRAuxiliar";
import { useReactToPrint } from "react-to-print";
import { PrinterOutlined } from "@ant-design/icons";
import React from "react";
import { EmpresaModel } from "../../../models/empresa/empresaModel";
import { buscarEmpresa } from "../../../services/empresaService";
import { listarSaldoConsignatario } from "../../../services/prestacaoContaService";
import { BuscarSaldoConsignatarioModel } from "../../../models/lancamentoConsignatario/buscarSaldoConsignatarioModel";
import TableComponent from "../../../components/table/tableComponent";
import dayjs from "dayjs";

dayjs.locale("pt-br");

const SaldoConsignatarioRelatorio = () => {
  //estados globais
  const empresaState = useAppSelector((state) => state.empresa);
  const prestacaoContaState = useAppSelector((state) => state.prestacaoConta);
  const persistedState = useAppSelector((state) => state.persisted);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const relatorioSaldoConsignatario = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(setTitle("RELATÓRIO DE SALDO DOS CONSIGNATÁRIOS"));
    dispatch(buscarEmpresa(empresaState.busca));
    if (persistedState.user.empresa !== null) {
      onChangeEmpresa(persistedState.user.empresa.idEmpresa);
    }
  }, []);

  const onChangeEmpresa = (value: number) => {
    let busca: BuscarSaldoConsignatarioModel = { ...prestacaoContaState.busca, idEmpresa: value };

    dispatch(setBuscaPrestacaoConta(busca));
  };

  const onChangeData = (value: DatePickerProps["value"], dateString: string | string[]) => {
    let busca: BuscarSaldoConsignatarioModel = { ...prestacaoContaState.busca, dataFim: dayjs(value).toDate() };

    dispatch(setBuscaPrestacaoConta(busca));
    dispatch(listarSaldoConsignatario(busca));
  };

  const somatorio = prestacaoContaState.listSaldo.reduce(
    (acumulador, conta) => {
      acumulador.saldo += conta.saldo || 0;
      return acumulador;
    },
    {
      saldo: 0,
    }
  );

  const handlePrint = useReactToPrint({
    content: () => relatorioSaldoConsignatario.current,
  });

  const columns: ColumnsType<SaldoConsignatarioModel> = [
    {
      title: "CONSIGNATÁRIO",
      dataIndex: "apelido",
    },
    {
      title: "SALDO",
      dataIndex: "saldo",
      align: "right",
      render: (_, record) =>
        record.saldo < 0 ? <p style={{ color: "red", marginBottom: "0px" }}>{currencyBRL(record.saldo)}</p> : <p style={{ color: "blue", marginBottom: "0px" }}>{currencyBRL(record.saldo)}</p>,
    },
  ];

  const RelatorioHtml = React.forwardRef<HTMLDivElement>((_props, ref) => {
    return (
      <div ref={ref} className="relatorios-html-format">
        <div className="rel-cabecalho">
          <div className="rel-titulo">
            <h2>
              <strong>Saldo dos consignatários</strong>
            </h2>
          </div>
          <hr />
          <p>
            {"Empresa: " +
              empresaState.list.filter((a: EmpresaModel) => a.id === prestacaoContaState.busca.idEmpresa)[0]?.apelido +
              "; Data do saldo: " +
              dayjs(prestacaoContaState.busca.dataFim).format("DD/MM/YYYY")}
          </p>
        </div>
        <div className="rel-detalhes">
          <table className="tb-dados">
            <thead>
              <tr className="header-table">
                <th className="ht-left">Consignatário</th>
                <th className="ht-right">Saldo</th>
              </tr>
            </thead>
            <tbody className="body-table">
              {prestacaoContaState.listSaldo.map((t) => {
                return (
                  <React.Fragment key={t.id}>
                    <tr>
                      <td className="ht-left">{t.apelido}</td>
                      <td className="ht-right">{currencyBRL(t.saldo)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="footer-table">
              <tr>
                <td className="ht-left">SALDO TOTAL:</td>
                <td className="ht-right">{currencyBRL(somatorio.saldo)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  });

  return (
    <Flex vertical>
      <Form layout="vertical">
        <Row gutter={12} align="bottom">
          <Col span={20}>
            <Flex gap={12}>
              {persistedState.user.empresa === null && (
                <Form.Item
                  label="Empresa"
                  name="empresa"
                  rules={[
                    {
                      required: true,
                      message: "Selecione uma empresa",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="labelSearch"
                    loading={empresaState.loading}
                    onChange={onChangeEmpresa}
                    style={{ width: 300 }}
                    options={empresaState.list.map((a: EmpresaModel) => ({
                      value: a.id,
                      key: a.apelido,
                      label: (
                        <Flex justify="flex-start" align="center" gap={8} style={{ padding: "1px 0px" }}>
                          <Flex style={{ lineHeight: "0px" }}>{a.urlLogotipoPequeno && <Image width={25} height={25} style={{ lineHeight: "0px" }} src={a.urlLogotipoPequeno} preview={false} />}</Flex>
                          {a.apelido}
                        </Flex>
                      ),
                      labelSearch: a.apelido,
                    }))}
                  />
                </Form.Item>
              )}
              <Form.Item
                label="Data do saldo"
                name="dataSaldo"
                rules={[
                  {
                    required: true,
                    message: "Campo obrigatório",
                  },
                ]}
              >
                <DatePicker placeholder="" onChange={onChangeData} format="DD/MM/YYYY" style={{ width: 120 }} />
              </Form.Item>
            </Flex>
          </Col>
          <Col span={4}>
            <Flex justify="end">
              <Form.Item name="botaoCarregar" label=" ">
                <Button icon={<PrinterOutlined />} onClick={handlePrint} disabled={prestacaoContaState.loading}></Button>
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
      <TableComponent
        columns={columns}
        data={prestacaoContaState.listSaldo}
        loading={prestacaoContaState.loading}
        tableProps={{
          summary: (pageData: any) =>
            pageData.length > 0 && (
              <Table.Summary.Row className="rowFooterCls">
                <Table.Summary.Cell index={0}>SALDO TOTAL:</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <span style={{ color: somatorio.saldo > 0 ? "blue" : "red", marginBottom: "0px" }}>{currencyBRL(somatorio.saldo)}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ),
        }}
      />
      <div style={{ display: "none" }}>
        <RelatorioHtml ref={relatorioSaldoConsignatario}></RelatorioHtml>
      </div>
    </Flex>
  );
};

export default SaldoConsignatarioRelatorio;
